import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField,
    Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { personName } from '../../voice/callFormat';
import { missedCallsApi, errorMessage } from './missedCallsApi';
import DuplicateChoice from './DuplicateChoice';
import DayChoice from './DayChoice';
import {
    DUPLICATE_CONFLICT_TEXT, WHO_LABEL, applyDuplicateConflicts, dayPhrase, duplicateConflictsOf, formatListDate, keptMatches, matchKey,
    nyNowHHmm, parseNumberBlock, reviewRowError, toConfirmRow, toPreviewRow,
} from './missedCallFormat';

const PREVIEW_DEBOUNCE_MS = 500;
const MAX_ROWS = 50;
const MATCHES_SHOWN = 5;   // a number on many records (or a shared dev number) shouldn't fill the dialog

// A filled-in time follows the day (`time_auto`): now for today, blank — 시간 모름 — for a past day's list (plan §3.9).
const blank = (pastDay = false, now = new Date()) => ({
    key: `typed-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    include: true, phone_number: '', raw_label: '', missed_time: pastDay ? '' : nyNowHHmm(now), time_auto: true, missed_count: 1,
    on_duplicate: 'merge', candidate: null, dropped_matches: [],
});

/** Who this number belongs to, with an ✕ on each. Long lists stay collapsed until asked for. */
const WhoMatches = ({ row, onDrop }) => {
    const [showAll, setShowAll] = useState(false);
    const kept = keptMatches(row);
    const dropped = row.dropped_matches?.length || 0;
    if (!row.candidate) return null;
    if (!kept.length) {
        return (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {dropped ? '지정한 사람 없음 — 모름으로 저장됩니다' : '이 번호를 가진 학생·선생님이 없습니다'}
                {dropped > 0 && (
                    <Button size="small" sx={{ ml: 0.5, textTransform: 'none' }} onClick={() => onDrop(null)}>되돌리기</Button>
                )}
            </Typography>
        );
    }
    const shown = showAll ? kept : kept.slice(0, MATCHES_SHOWN);
    return (
        <Box sx={{ mt: 0.5 }}>
            {shown.map((m) => (
                <Box key={matchKey(m)} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Typography variant="caption">
                        {WHO_LABEL[m.contact_label] || ''} · {personName(m)}{m.consultation_number ? ` #${m.consultation_number}` : ''}
                    </Typography>
                    <Tooltip title="이 사람 빼기">
                        <IconButton size="small" sx={{ p: 0.125 }} onClick={() => onDrop(matchKey(m))}>
                            <CloseIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ))}
            {kept.length > MATCHES_SHOWN && (
                <Button size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => setShowAll((v) => !v)}>
                    {showAll ? '접기' : `외 ${kept.length - MATCHES_SHOWN}명 더 보기`}
                </Button>
            )}
            {dropped > 0 && (
                <Button size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0, ml: 1 }} onClick={() => onDrop(null)}>
                    {dropped}명 뺌 · 되돌리기
                </Button>
            )}
        </Box>
    );
};

/**
 * Rows an admin types in rather than uploads (docs/MISSED_CALLS_PLAN.md §3.8) — a number written on a sticky
 * note, a call the screenshots missed, a callback someone asked for. Paste a block of numbers or add them one
 * at a time; the time defaults to now in the office's zone.
 *
 * As soon as a number is readable the server says who has it on file and whether it is already on today's
 * list; a number already there has to answer 합치기 / 새 행 / 무시 before 저장, exactly as in the screenshot
 * review. Nothing is written until 저장.
 */
const AddRowsDialog = ({ open, onClose, config, onSaved }) => {
    const theme = useTheme();
    const phone = useMediaQuery(theme.breakpoints.down('sm'));
    const [rows, setRows] = useState([blank()]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const timer = useRef(null);
    const seq = useRef(0);
    const [listDate, setListDate] = useState(null);   // null = today; a past day's list (plan §3.9)
    const pastDay = Boolean(listDate && config?.today && listDate < config.today);

    useEffect(() => {
        if (!open) return;
        setRows([blank()]);
        setListDate(null);
        setError(null);
    }, [open]);

    const changeDay = (date) => {
        const past = Boolean(date && config?.today && date < config.today);
        setListDate(date);
        setRows((prev) => prev.map((r) => (r.time_auto ? { ...r, missed_time: past ? '' : nyNowHHmm() } : r)));
    };

    const setRow = useCallback((key, patch) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r))), []);

    // Ask the server what these numbers are and whether the day already has them. Debounced, and the answer
    // is merged by number so a reply that lands after more typing can't overwrite the newer text.
    const numbersKey = rows.map((r) => r.phone_number.trim()).join('|');
    useEffect(() => {
        if (!open) return undefined;
        if (timer.current) clearTimeout(timer.current);
        const ready = rows.filter((r) => reviewRowError({ ...r, on_duplicate: 'merge' }, { timeOptional: pastDay }) === null);
        if (!ready.length) return undefined;
        const mine = ++seq.current;
        timer.current = setTimeout(async () => {
            try {
                const { candidates } = await missedCallsApi.previewRows(ready.map(toPreviewRow), listDate);
                if (mine !== seq.current) return;
                const byNumber = new Map((candidates || []).map((c, i) => [ready[i].phone_number.trim(), c]));
                setRows((prev) => prev.map((r) => {
                    const c = byNumber.get(r.phone_number.trim());
                    if (!c) return r.candidate ? { ...r, candidate: null, on_duplicate: 'merge' } : r;
                    // Keep an answer the admin already gave for this same collision.
                    const sameNumber = r.candidate?.phone_number === c.phone_number;
                    const keep = r.candidate?.merge_into?.id === c.merge_into?.id ? r.on_duplicate : c.on_duplicate;
                    return {
                        ...r,
                        candidate: c,
                        on_duplicate: c.merge_into ? keep : 'merge',
                        dropped_matches: sameNumber ? r.dropped_matches : [],   // a different number, a different Who
                    };
                }));
            } catch {
                /* the save re-validates anyway */
            }
        }, PREVIEW_DEBOUNCE_MS);
        return () => clearTimeout(timer.current);
    }, [open, numbersKey, listDate]);   // eslint-disable-line react-hooks/exhaustive-deps

    /** Pasting a block of numbers fills a row each, instead of one long unusable line. */
    const onPaste = (key) => (e) => {
        const text = e.clipboardData?.getData('text') || '';
        if (!/[\n,;]/.test(text)) return;
        e.preventDefault();
        const added = parseNumberBlock(text).map((r) => ({ ...r, time_auto: true, ...(pastDay ? { missed_time: '' } : {}) }));
        setRows((prev) => {
            const at = prev.findIndex((r) => r.key === key);
            const rest = prev.filter((r) => r.key !== key || r.phone_number.trim());
            const next = [...rest.slice(0, at < 0 ? rest.length : at), ...added, ...rest.slice(at < 0 ? rest.length : at)];
            return next.slice(0, MAX_ROWS);
        });
    };

    const errors = rows.map((r) => reviewRowError(r, { timeOptional: pastDay }));
    const filled = rows.filter((r) => r.phone_number.trim());
    const undecided = rows.filter((r) => r.candidate?.merge_into && !r.on_duplicate).length;
    const canSave = !busy && filled.length > 0 && !errors.some(Boolean);

    // Typed numbers aren't thrown away by a stray click outside, or Esc. X and 취소 still close.
    const onDialogClose = (event, reason) => {
        if (busy) return;
        if ((reason === 'backdropClick' || reason === 'escapeKeyDown') && filled.length) return;
        onClose();
    };

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            const out = await missedCallsApi.addRows(filled.map(toConfirmRow).filter(Boolean), listDate);
            onSaved(out);
            onClose();
        } catch (err) {
            // Someone added one of these numbers since the preview: nothing was saved, ask again for those rows.
            const conflicts = duplicateConflictsOf(err);
            if (conflicts) setRows((prev) => applyDuplicateConflicts(prev, conflicts));
            setError(conflicts ? DUPLICATE_CONFLICT_TEXT : errorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const today = formatListDate(listDate || config?.today);   // the list these rows go under
    return (
        <Dialog open={open} onClose={onDialogClose} fullWidth maxWidth="sm" fullScreen={phone} disableEnforceFocus>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 0.5 }}>
                <Box sx={{ flex: 1 }}>
                    번호로 직접 추가
                    <Typography variant="body2" color="text.secondary">
                        {today ? `${today} 목록에 ` : ''}스크린샷 없이 번호만 넣어 행을 만듭니다. 여러 줄을 붙여넣으면 한 줄에 하나씩 들어갑니다.
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose} disabled={busy}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {config?.today && (
                    <Box sx={{ mb: 2 }}>
                        <DayChoice today={config.today} pastDays={config.past_days} value={listDate} onChange={changeDay} disabled={busy} />
                        {pastDay && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                                시간을 모르면 비워 두세요 — "시간 모름"으로 저장됩니다.
                            </Typography>
                        )}
                    </Box>
                )}
                {rows.map((r, i) => {
                    const c = r.candidate;
                    return (
                        <Box key={r.key} sx={{ mb: 1.5, pb: 1.5, borderBottom: i < rows.length - 1 ? '1px solid' : 0, borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                    label="번호"
                                    value={r.phone_number}
                                    onChange={(e) => setRow(r.key, { phone_number: e.target.value })}
                                    onPaste={onPaste(r.key)}
                                    placeholder="(201) 555-1234"
                                    size="small"
                                    autoFocus={i === 0}
                                    error={Boolean(errors[i]) && errors[i].startsWith('번호')}
                                    inputProps={{ inputMode: 'tel' }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1, ...(phone && { '& .MuiInputBase-input': { fontSize: 16 } }) }}
                                />
                                <TextField
                                    label="부재 시간"
                                    type="time"
                                    value={r.missed_time}
                                    onChange={(e) => setRow(r.key, { missed_time: e.target.value, time_auto: false })}
                                    size="small"
                                    error={Boolean(errors[i]) && errors[i].startsWith('시간')}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ width: 130, ...(phone && { '& .MuiInputBase-input': { fontSize: 16 } }) }}
                                />
                                <Tooltip title="이 줄 지우기">
                                    <span>
                                        <IconButton
                                            size="small"
                                            sx={{ mt: 0.5 }}
                                            disabled={rows.length === 1}
                                            onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>

                            <WhoMatches
                                row={r}
                                onDrop={(key) => setRow(r.key, {
                                    dropped_matches: key === null ? [] : [...(r.dropped_matches || []), key],
                                })}
                            />

                            <DuplicateChoice candidate={c} value={r.on_duplicate} onChange={(v) => setRow(r.key, { on_duplicate: v })} disabled={busy} boxed timeUnknown={pastDay && !r.missed_time} />

                            {errors[i] && <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>{errors[i]}</Typography>}
                        </Box>
                    );
                })}

                <Button
                    startIcon={<AddIcon />}
                    sx={{ textTransform: 'none' }}
                    disabled={rows.length >= MAX_ROWS}
                    onClick={() => setRows((prev) => [...prev, blank(pastDay)])}
                >
                    번호 추가
                </Button>

                {undecided > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        {dayPhrase(listDate || config?.today, config?.today)} 이미 있는 번호가 {undecided}건 있습니다. 합치기 / 새 행 / 무시를 고르면 저장할 수 있습니다.
                    </Alert>
                )}
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={busy}>취소</Button>
                <Tooltip title={undecided ? '같은 번호에서 하나를 골라 주세요' : ''}>
                    <span>
                        <Button variant="contained" onClick={save} disabled={!canSave}>
                            {busy ? '저장 중…' : undecided ? `${undecided}건 선택 필요` : `저장 (${filled.length})`}
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

export default AddRowsDialog;
